import React from 'react';

interface State {
    hasError: boolean;
}

/**
 * Top-level error boundary.
 * Catches React reconciliation crashes (e.g. caused by browser auto-translation
 * mutating the DOM) and shows a friendly recovery screen instead of a blank page.
 */
class AppErrorBoundary extends React.Component<React.PropsWithChildren, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error('[AppErrorBoundary]', error, info);
    }

    handleReload = () => {
        // Reset boundary state first so a full reload can re-mount cleanly
        this.setState({ hasError: false }, () => {
            window.location.reload();
        });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-6">
                    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                        <div className="flex justify-center mb-4">
                            <div className="bg-amber-100 rounded-full p-4">
                                <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                </svg>
                            </div>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
                        <p className="text-gray-500 text-sm mb-2">
                            This can happen when your browser's <strong>auto-translate</strong> feature is active.
                        </p>
                        <p className="text-gray-400 text-xs mb-6">
                            Please <strong>disable auto-translation</strong> for this page, then reload. Your progress may be saved.
                        </p>
                        <button
                            onClick={this.handleReload}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl transition"
                        >
                            Reload Page
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

export default AppErrorBoundary;
